import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { defaultHome } from "@/components/ProtectedRoute";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { principal, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (principal) return <Navigate to={defaultHome(principal)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const res = await login(email, password, rememberMe);
    setLoading(false);
    if (res.ok) nav(defaultHome(res.principal));
    else setErr(res.error);
  };

  return (
    <div className="min-h-screen flex bg-[#050505]">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-[#262626] relative overflow-hidden">
        <div className="z-10">
          <img src="/branding/interazzo-logo.png" alt="Interazzo" className="w-40 h-auto object-contain" />
        </div>

        <div className="z-10">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-6">Command Console</p>
          <h1 className="font-display text-6xl leading-[1.05] text-white mb-6">
            Orchestrate<br/>
            <span className="italic text-[#d4af37]">every</span> role.
          </h1>
          <p className="text-gray-400 max-w-md leading-relaxed">
            A minimalist command center for user, role, hierarchy and marketing operations —
            engineered for clarity, executed in black and gold.
          </p>
        </div>

        <div className="z-10 flex items-center gap-8 text-[10px] font-mono uppercase tracking-widest text-gray-500">
          <span>v1.1 · Marketing Suite</span>
          <span>·</span>
          <span>Secure Session</span>
        </div>

        <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-[#d4af37]/10" />
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full border border-[#d4af37]/20" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Sign In</p>
            <h2 className="font-display text-4xl text-white">Welcome back.</h2>
            <p className="text-gray-500 text-sm mt-2">Administer or operate your workspace.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" strokeWidth={1.5}/>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="iz-input pl-10" data-testid="login-email-input" required/>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" strokeWidth={1.5}/>
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="iz-input pl-10 pr-10" data-testid="login-password-input" required/>
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#d4af37] transition-colors"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        data-testid="login-password-toggle">
                  {showPassword
                    ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/>
                    : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
                </button>
              </div>
            </div>

            {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2" data-testid="login-error">{err}</div>}

            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="accent-[#d4af37]" data-testid="remember-me"/>
              Remember me for 30 days
            </label>

            <button type="submit" disabled={loading} className="iz-btn-primary w-full" data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
